/**
 * Catalog Insights data layer.
 *
 * Talks to GET /platforms/catalog, which answers one product question:
 * "how much does a platform lean on exclusive content, and is that bet growing?"
 *
 * The endpoint returns both halves of the macro→micro view in a single call:
 *   - macro: every platform's exclusive-vs-full totals for one quarter + region
 *   - micro: the selected platform's exclusive-vs-full over the last N quarters
 * each split TV vs movies, with all three measures per bar so the UI can toggle
 * (Revenue / Subscriber-equiv / Titles) without refetching.
 */
import { apiUrl, getJson } from './api';

/** The three interchangeable measures every bar carries. */
export type CatalogMeasure = 'revenue' | 'subscribers_equivalent' | 'titles';

export type ContentType = 'show' | 'movie';

/** One bar's worth of numbers — the same shape for exclusive and for full. */
export interface CatalogMeasures {
  revenue: number;
  subscribers_equivalent: number;
  titles: number;
}

/** A platform's exclusive-vs-full split for one content type (macro row). */
export interface CatalogPlatformSplit {
  platform: string;
  exclusive: CatalogMeasures;
  full: CatalogMeasures;
}

/** One quarter's exclusive-vs-full split for one content type (micro row). */
export interface CatalogQuarterSplit {
  quarter: string;
  endDate: string;
  exclusive: CatalogMeasures;
  full: CatalogMeasures;
}

export interface CatalogMacro {
  endDate: string;
  quarter: string;
  show: CatalogPlatformSplit[];
  movie: CatalogPlatformSplit[];
}

export interface CatalogMicro {
  platform: string;
  show: CatalogQuarterSplit[];
  movie: CatalogQuarterSplit[];
}

export interface CatalogResponse {
  macro: CatalogMacro;
  micro: CatalogMicro;
  meta: {
    platform: string;
    region: string;
    endDate: string;
    lastQuarters: number;
    measures: CatalogMeasure[];
  };
}

export interface CatalogParams {
  platform: string;
  /** '' means all regions (the endpoint omits the region filter). */
  region: string;
  /** '' means latest quarter (the endpoint defaults to it). */
  endDate: string;
  lastQuarters?: number;
}

export async function fetchCatalog(params: CatalogParams): Promise<CatalogResponse> {
  const search = new URLSearchParams();
  if (params.platform) search.set('platform', params.platform);
  if (params.region) search.set('region', params.region);
  if (params.endDate) search.set('endDate', params.endDate);
  if (params.lastQuarters) search.set('lastQuarters', String(params.lastQuarters));
  return getJson<CatalogResponse>(apiUrl(`/platforms/catalog?${search}`));
}

/** Human label + how to format each measure, kept beside the type so the UI stays declarative. */
export const MEASURE_META: Record<CatalogMeasure, { label: string; short: string }> = {
  revenue: { label: 'Revenue', short: 'Revenue' },
  subscribers_equivalent: { label: 'Subscriber Equivalent', short: 'Subs Equiv.' },
  titles: { label: 'Titles', short: 'Titles' },
};

/**
 * Exclusive as a share of full, for the headline "exclusives = X% of …" takeaway.
 * Returns null when full is zero so the caller renders — rather than 0%.
 */
export function exclusiveShare(split: { exclusive: CatalogMeasures; full: CatalogMeasures }, measure: CatalogMeasure): number | null {
  const full = split.full[measure];
  if (!full) return null;
  return split.exclusive[measure] / full;
}
